import React, { ReactNode, useCallback, useContext, useState } from "react";

import { MeterFooter } from "@/common/Footer";
import { renderOutro } from "@/common/Outro";
import { Button } from "@/components/Form";
import Header from "@/components/Header";
import { SharingType } from "@/components/Sharing";
import { Subline } from "@/components/Subline";
import { GlobalContext } from "@/contexts";
import LocaleContext from "@/i18n";
import { Opinion, OpinionResult, OpinionVote } from "@/service/Model";
import Svg from "@/styles/utils/Svg";
import Markdown from "@/utils/markdown";
import { handleShare } from "@/utils/socialActions";

import { isEnded } from "../MeterConstants";
import {
  EditorialOpinionText,
  Flexbox,
  InnerBox,
  Main,
  MeterAnswerLeft,
  MeterAnswerRight,
  MeterQuestions,
} from "../styles";

export type MeterTemplateProps = {
  meter: Opinion;
  icons: IIcons;
  results: OpinionResult;
  showOutro: boolean;
  hasVoted: boolean;
  onConfirmed: () => void;
  onOpinionClose: () => void;
  onOutroClose: () => void;
  onRegistation?: (
    event: React.FormEvent<HTMLFormElement>,
    form: any
  ) => Promise<any>;
  editorialOpinionIndex: number;
  editorialOpinionOpen: boolean;
  tealiumConfig?: ITealium;
  children: ReactNode;
};

export const MeterTemplate: React.FC<MeterTemplateProps> = (
  props: MeterTemplateProps
): React.JSX.Element => {
  const [confirmed, setConfirmed] = useState<boolean>(false);

  const globalContext = useContext(GlobalContext);
  const localeContext = useContext(LocaleContext);
  const { translation } = localeContext;

  const {
    children,
    results,
    icons,
    showOutro,
    editorialOpinionOpen,
    editorialOpinionIndex,
    meter,
    hasVoted,
  } = props;

  const displayOutro =
    !editorialOpinionOpen && showOutro && meter.outro && !isEnded(meter);

  const handleClose = (e: React.SyntheticEvent): void => {
    e.preventDefault();
    setConfirmed(true);
    props.onConfirmed();
  };

  const renderTitle = (): React.JSX.Element => {
    return (
      <Header visible={true} icons={icons}>
        {meter.title}
        {isEnded(meter) && (
          <div className="meterSubtitle">
            Die Abstimmung wurde bereits beendet.
          </div>
        )}
        {!isEnded(meter) && meter.subtitle && (
          <div className="meterSubtitle">{meter.subtitle}</div>
        )}
      </Header>
    );
  };

  const renderQuestions = (): React.JSX.Element => {
    return (
      <MeterQuestions>
        <MeterAnswerLeft>
          <div
            dangerouslySetInnerHTML={{
              __html: Markdown(meter.answerLeft),
            }}
          />
        </MeterAnswerLeft>
        <MeterAnswerRight>
          <div
            dangerouslySetInnerHTML={{
              __html: Markdown(meter.answerRight),
            }}
          />
        </MeterAnswerRight>
      </MeterQuestions>
    );
  };

  const renderEditorialOpinion = (): React.JSX.Element => {
    return (
      <div>
        {(meter.opinions || []).map(
          (opinion: OpinionVote, index: number): React.JSX.Element => {
            if (editorialOpinionIndex !== index) {
              return <div key={`meter-${index}`} />;
            }
            return (
              <div
                key={`meter-${index}`}
                className="meterEditorialOpinionWrapper"
                onClick={props.onOpinionClose}
              >
                <div className="meterEditorialOpinion">
                  <div
                    className="meterButtonClose"
                    onClick={props.onOpinionClose}
                  >
                    <Svg contents={icons.Close} height={14} width={14} />
                  </div>
                  <div className="meterEditorialOpinionText">
                    <div className="meterEditorialNameIconWrapper">
                      <span className="meterEditorialOpinionName">
                        {opinion.name}
                      </span>
                    </div>
                    <div className="meterEditorialOpinionDescription">
                      <div className="meterEditorialIcon">
                        <img
                          alt="editorial-icon"
                          src={`${opinion.iconUrl}?width=174&height=174`}
                        />
                      </div>
                      <EditorialOpinionText>
                        „{opinion.description}“
                      </EditorialOpinionText>
                    </div>
                  </div>
                  {opinion.opinionOutLink && (
                    <Button>
                      <a
                        href={opinion.opinionOutLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {translation.toArticle}
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            );
          }
        )}
      </div>
    );
  };

  const renderIntro = (): React.JSX.Element => {
    if (confirmed || hasVoted || isEnded(meter)) {
      return <span />;
    }

    return (
      <a onClick={handleClose}>
        <Flexbox>
          <Subline showCloseButton={true} overlay={true}>
            {"Stimmen Sie mit dem Regler oder per Klick auf einen Balken ab"}
          </Subline>
          <div className="meterButtonOk" onClick={handleClose}>
            <Svg contents={icons.CloseLite} height={10} width={10} />
          </div>
        </Flexbox>
      </a>
    );
  };

  const handleShareClick = useCallback((type: SharingType) => {
    handleShare(type, meter.title, globalContext.brandSettings?.xHandle);
  }, []);

  return (
    <div>
      {renderTitle()}
      <InnerBox>
        <Main>
          {renderIntro()}
          <div>{children}</div>
          {renderQuestions()}
          {renderEditorialOpinion()}
          {displayOutro &&
            meter.outro &&
            renderOutro(
              meter.outro,
              { globals: globalContext },
              props.onOutroClose
            )}
        </Main>
        <MeterFooter
          participants={results.participantsCount || 0}
          onShareClick={handleShareClick}
        />
      </InnerBox>
    </div>
  );
};

export default MeterTemplate;
